import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  Divider,
  IconButton,
  OutlinedInput,
  Tooltip,
  CircularProgress,
  Alert,
  Checkbox,
} from "@mui/material";
import {
  Close as CloseIcon,
  Info as InfoIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";

// ── Design System Imports ──────────────────────────────────────────────────────
import typography from "../../cssUtils/typography";
import {
  ColorsConstants,
  borderColors,
  grayFontColor,
  inputColor,
} from "../../cssUtils/constants";

// ── Shared Components ──────────────────────────────────────────────────────────
import ButtonOne from "../../common/normal-button";
import ButtonThree from "../../common/third-button";
import { CallingCodeSelect } from "../../common/country-code-select";
import { getCountryForCallingCode, splitPhoneNumber } from "../../common/country-codes";
import { getPhoneValidationError } from "../../common/phone-validation";

// ── API Imports ───────────────────────────────────────────────────────────────
import { GET_RCS_BOTS, POST_RCS_BOTS,GET_RCS_BRAND } from "../../config/urls";
import axios from "axios";
import ButtonTwo from "../../common/dashboard-button";
import { useAuth } from "../../hooks/AuthProvider";

// ─────────────────────────────────────────────────────────────────────────────
// Design tokens
// ─────────────────────────────────────────────────────────────────────────────
const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "8px",
    backgroundColor: ColorsConstants.primaryWhite,
    color: inputColor,
    ...typography.bodyLarge,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.inputFieldGrey },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryGray50 },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryBlue100 },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.error },
  },
  "& .MuiInputBase-input": { ...typography.bodyLarge, color: inputColor, padding: "9px 12px" },
  "& .MuiFormHelperText-root": { ...typography.bodySmall, ml: 0, mt: "4px" },
};

const MULTILINE_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: ColorsConstants.primaryWhite,
    color: inputColor,
    ...typography.bodyLarge,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.inputFieldGrey },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryGray50 },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryBlue100 },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.error },
  },
  "& .MuiInputBase-input": { ...typography.bodyLarge, color: inputColor },
  "& .MuiFormHelperText-root": { ...typography.bodySmall, ml: 0, mt: "4px" },
};

const SELECT_SX = {
  height: 40,
  borderRadius: "8px",
  backgroundColor: ColorsConstants.primaryWhite,
  color: inputColor,
  ...typography.bodyLarge,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.inputFieldGrey },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryGray50 },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryBlue100 },
  "& .MuiSelect-select": { ...typography.bodyLarge, color: inputColor, padding: "9px 12px" },
};

const RADIO_SX = {
  color: ColorsConstants.inputFieldGrey,
  "&.Mui-checked": { color: ColorsConstants.primaryPurple100 },
  padding: "3px 8px 3px 0",
};

const CARD_SX = {
  backgroundColor: ColorsConstants.primaryWhite,
  border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
  borderRadius: "12px",
  p: 3,
};

const SECTION_TITLE_SX = {
  ...typography.bodyLarge,
  fontWeight: 700,
  color: ColorsConstants.primaryBlue100,
  mb: 2,
};

const LABEL_SX = {
  ...typography.bodyLarge,
  fontWeight: 600,
  color: ColorsConstants.primaryBlue100,
  mb: 0.5,
  display: "flex",
  alignItems: "center",
  gap: 0.5,
};

const HINT_SX = {
  ...typography.bodySmall,
  color: grayFontColor,
  mb: 1,
};

// ─────────────────────────────────────────────────────────────────────────────
// Async Image Validator
// ─────────────────────────────────────────────────────────────────────────────
const validateImageAsync = (file, maxSizeKB, reqWidth, reqHeight) => {
  return new Promise((resolve) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      return resolve(`Invalid format. Only JPG, JPEG, and PNG are supported.`);
    }
    if (file.size > maxSizeKB * 1024) {
      return resolve(`File is too large. Maximum allowed size is ${maxSizeKB}KB.`);
    }
    const img = new Image();
    img.onload = () => {
      if (img.width !== reqWidth || img.height !== reqHeight) {
        resolve(`Image must be exactly ${reqWidth}x${reqHeight} px (You uploaded ${img.width}x${img.height} px).`);
      } else {
        resolve(null);
      }
    };
    img.onerror = () => resolve("Failed to process the image file.");
    img.src = URL.createObjectURL(file);
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper Components
// ─────────────────────────────────────────────────────────────────────────────
function FieldLabel({ children, required, hint }) {
  return (
    <Box sx={{ mb: 0.75 }}>
      <Typography sx={LABEL_SX}>
        {children}
        {required && <Typography component="span" sx={{ color: ColorsConstants.error }}>*</Typography>}
        {hint && (
          <Tooltip title={hint} placement="top" arrow>
            <InfoIcon sx={{ fontSize: 14, color: grayFontColor, cursor: "help" }} />
          </Tooltip>
        )}
      </Typography>
    </Box>
  );
}

function ImageUploadZone({ aspectLabel, value, preview, onChange, error }) {
  const inputRef = React.useRef();
  const [dragging, setDragging] = useState(false);

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png"
        style={{ display: "none" }}
        onChange={(e) => {
          onChange(e.target.files[0]);
          e.target.value = "";
        }}
      />
      <Box
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); onChange(e.dataTransfer.files[0]); }}
        sx={{
          width: "100%", minHeight: 90,
          border: `1.5px dashed ${error ? ColorsConstants.error : dragging ? ColorsConstants.primaryPurple100 : ColorsConstants.inputFieldGrey}`,
          borderRadius: "8px",
          backgroundColor: dragging ? ColorsConstants.primaryPurple60 : ColorsConstants.primaryBlue60,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          cursor: "pointer", overflow: "hidden", transition: "all 0.15s", p: 1,
        }}
      >
        {preview ? (
          <Box component="img" src={preview} sx={{ width: "100%", height: "100%", objectFit: "contain", maxHeight: 120 }} />
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 28, color: grayFontColor, mb: 0.5 }} />
            <Typography sx={{ ...typography.bodySmall, color: grayFontColor, textAlign: "center" }}>Click or drag to upload</Typography>
            {aspectLabel && <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray40, mt: 0.25 }}>{aspectLabel}</Typography>}
          </>
        )}
      </Box>
      {preview && <ButtonThree onClick={() => onChange(null)} sx={{ mt: 1, height: 30, ...typography.bodySmall }}>Remove</ButtonThree>}
      {error && <Typography sx={{ ...HINT_SX, color: ColorsConstants.error, mt: 0.5 }}>{error}</Typography>}
    </Box>
  );
}

function PhonePreview({ botName, description, logoPreview, bannerPreview, color }) {
  const headerBg = color && color !== "#000000" ? color : ColorsConstants.primaryBlue100;
  return (
    <Box sx={{ width: 180, mx: "auto" }}>
      <Box sx={{ backgroundColor: ColorsConstants.primaryGray80, borderRadius: "22px", p: "10px 8px", boxShadow: "0 12px 40px rgba(8,36,92,0.22)" }}>
        <Box sx={{ backgroundColor: ColorsConstants.primaryWhite, borderRadius: "14px", overflow: "hidden" }}>
          <Box sx={{ backgroundColor: ColorsConstants.primaryGray80, px: 1.5, py: "4px", display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ ...typography.bodySmall, fontSize: "8px", color: ColorsConstants.primaryWhite }}>9:41</Typography>
            <Typography sx={{ ...typography.bodySmall, fontSize: "8px", color: ColorsConstants.primaryWhite }}>●●●</Typography>
          </Box>
          <Box sx={{ backgroundColor: headerBg, px: 1.5, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.2)", overflow: "hidden", flexShrink: 0 }}>
              {logoPreview && <Box component="img" src={logoPreview} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />}
            </Box>
            <Box>
              <Typography sx={{ ...typography.bodySmall, fontSize: "9px", fontWeight: 700, color: ColorsConstants.primaryWhite }}>{botName || "Bot Name"}</Typography>
              <Typography sx={{ ...typography.bodySmall, fontSize: "7px", color: "rgba(255,255,255,0.6)" }}>@username</Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}` }}>
            {["Info", "Options"].map((t, i) => (
              <Box key={t} sx={{ flex: 1, textAlign: "center", py: "5px", borderBottom: i === 0 ? `2px solid ${ColorsConstants.primaryPurple100}` : "2px solid transparent" }}>
                <Typography sx={{ ...typography.bodySmall, fontSize: "8px", fontWeight: i === 0 ? 700 : 500, color: i === 0 ? ColorsConstants.primaryPurple100 : grayFontColor }}>{t}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ width: "100%", height: 55, backgroundColor: ColorsConstants.primaryBlue70, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {bannerPreview ? <Box component="img" src={bannerPreview} sx={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Typography sx={{ ...typography.bodySmall, fontSize: "8px", color: grayFontColor }}>Banner</Typography>}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", mt: "-13px", mb: 0.5 }}>
            <Box sx={{ width: 26, height: 26, borderRadius: "50%", backgroundColor: ColorsConstants.primaryWhite, border: "2px solid white", overflow: "hidden", boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}>
              {logoPreview && <Box component="img" src={logoPreview} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />}
            </Box>
          </Box>
          <Box sx={{ px: 1.5, pb: 1, textAlign: "center" }}>
            <Typography sx={{ ...typography.bodySmall, fontSize: "9px", fontWeight: 700, color: ColorsConstants.primaryGray80 }}>{botName || "Bot name"}</Typography>
            <Typography sx={{ ...typography.bodySmall, fontSize: "7px", color: grayFontColor, lineHeight: 1.4, mb: 0.75 }}>{description || "Short description..."}</Typography>
            <Divider sx={{ mb: 0.75 }} />
            <Box sx={{ display: "flex", justifyContent: "space-around" }}>
              {["📞", "🌐", "✉️"].map((icon, i) => <Typography key={i} sx={{ fontSize: "10px" }}>{icon}</Typography>)}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function ErrMsg({ children }) {
  return children ? <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.error, mt: 0.5 }}>{children}</Typography> : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export { INPUT_SX, CARD_SX, SECTION_TITLE_SX, FieldLabel, ImageUploadZone, ErrMsg };

export default function RCSCreateBot({ bot, lockedBrand, onBack, onSuccess, onError }) {
  const isEditMode = !!bot;
  const { getValidToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(bot?.bot_logo || null);
  const [bannerPreview, setBannerPreview] = useState(bot?.banner_image || null);

  // 🟢 NEW: Brands State
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);

  const [phoneCode, setPhoneCode] = useState("+91");
  const [phoneNumberOnly, setPhoneNumberOnly] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(isEditMode);
  const [indianCarriersAccepted, setIndianCarriersAccepted] = useState(isEditMode);

  const [formData, setFormData] = useState({
    region: bot?.region || "INDIA",
    message_type: bot?.message_type || "PROMOTIONAL",
    billing_category: bot?.billing_category || "CONVERSATIONAL",
    bot_name: bot?.bot_name || "",
    brand: bot?.brand_details?.id || bot?.brand || lockedBrand?.id || "", // 🟢 UPDATED: Uses brand ID instead of brand_name string
    short_description: bot?.short_description || "",
    theme_color: bot?.theme_color || "#1976d2",
    primary_phone: bot?.primary_phone || "",
    phone_label: bot?.phone_label || "",
    primary_email: bot?.primary_email || "",
    email_label: bot?.email_label || "",
    primary_website: bot?.primary_website || "",
    website_label: bot?.website_label || "",
    terms_of_use_url: bot?.terms_of_use_url || "",
    privacy_policy_url: bot?.privacy_policy_url || "",
    rcs_api: bot?.rcs_api || "GOOGLE",
    languages_supported: bot?.languages_supported || "",
    status: bot?.status || "DRAFT",
    dotgo_bot_id: bot?.dotgo_bot_id || "",
  });

  // 🟢 NEW: Fetch Brands on Mount
  useEffect(() => {
    const fetchBrands = async () => {
      setLoadingBrands(true);
      try {
        const token = await getValidToken();
        const headers = { Authorization: `Bearer ${token}`, "X-Tenant-ID": localStorage.getItem("tenant") || "" };
        // NOTE: Adjust this URL path if your brands endpoint is mounted differently
        const response = await axios.get(GET_RCS_BRAND, { headers }); 
        
        // DRF generic views typically return paginated results, so we check for response.data.results
        const data = response.data.results || response.data;
        setBrands(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch brands", err);
      } finally {
        setLoadingBrands(false);
      }
    };
    fetchBrands();
  }, []);

  useEffect(() => {
    if (bot) {
      setFormData((prev) => ({ 
        ...prev, 
        ...bot,
        brand: bot.brand_details?.id || bot.brand || "" 
      }));
      if (bot.bot_logo) setLogoPreview(bot.bot_logo);
      if (bot.banner_image) setBannerPreview(bot.banner_image);

      if (bot.primary_phone) {
        const { countryCode, phone } = splitPhoneNumber(bot.primary_phone);
        if (bot.primary_phone.startsWith("+") && countryCode) { setPhoneCode(`+${countryCode}`); setPhoneNumberOnly(phone); }
        else { setPhoneNumberOnly(bot.primary_phone); }
      }
    }
  }, [bot]);

  const handleChange = (field) => (e) => {
    let value = e.target.value;
    if (field === "region" && value === "INDIA" && formData.message_type === "MULTI_USE") {
      setFormData((prev) => ({ ...prev, region: value, message_type: "PROMOTIONAL" }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleLogoChange = async (file) => {
    if (!file) {
      setLogoFile(null);
      setLogoPreview(null);
      return;
    }
    const err = await validateImageAsync(file, 90, 224, 224); 
    if (err) {
      setErrors((prev) => ({ ...prev, bot_logo: err }));
      setLogoFile(null);
      setLogoPreview(null);
    } else {
      setErrors((prev) => ({ ...prev, bot_logo: null }));
      setLogoFile(file);
      const r = new FileReader();
      r.onload = (e) => setLogoPreview(e.target.result);
      r.readAsDataURL(file);
    }
  };

  const handleBannerChange = async (file) => {
    if (!file) {
      setBannerFile(null);
      setBannerPreview(null);
      return;
    }
    const err = await validateImageAsync(file, 360, 1440, 448); 
    if (err) {
      setErrors((prev) => ({ ...prev, banner_image: err }));
      setBannerFile(null);
      setBannerPreview(null);
    } else {
      setErrors((prev) => ({ ...prev, banner_image: null }));
      setBannerFile(file);
      const r = new FileReader();
      r.onload = (e) => setBannerPreview(e.target.result);
      r.readAsDataURL(file);
    }
  };

  const validateName = (name) => {
    if (!name.trim()) return "Bot name is required";
    if (name.length > 40) return "Bot name must be 40 characters or less";
    return null;
  };

  const validateDescription = (desc) => {
    if (!desc.trim()) return "Short description is required";
    if (desc.length > 100) return "Description must be 100 characters or less";
    return null;
  };

  const validateUrl = (url) => {
    if (!url) return null;
    try { new URL(url); return null; } catch { return "Must be a valid URL (e.g., https://...)"; }
  };

  const validateEmail = (email) => {
    if (!email) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Must be a valid email address";
    return null;
  };

  const validateHexColor = (color) => {
    const hexRegex = /^#[0-9A-F]{6}$/i;
    if (!hexRegex.test(color)) return "Must be a valid hex color (e.g., #000000)";
    return null;
  };

  const validateLanguages = (languages) => {
    if (!languages.trim()) return "Languages are required";
    return null;
  };

  const validateForm = () => {
    const newErrors = {};

    const nameError = validateName(formData.bot_name);
    if (nameError) newErrors.bot_name = nameError;

    // 🟢 UPDATED: Validate Brand ID instead of Brand Name string
    // A brand still to be created (first bot) has no id yet, only a name
    if (!formData.brand && !lockedBrand) newErrors.brand = "Please select a brand from the list.";

    // Dotgo rejects agents whose name doesn't reflect the brand
    const brandName = brands.find((b) => b.id === formData.brand)?.brand_name || bot?.brand_details?.brand_name || lockedBrand?.brand_name || "";
    if (!newErrors.bot_name && brandName && !formData.bot_name.toLowerCase().includes(brandName.toLowerCase())) {
      newErrors.bot_name = `Bot name must include the brand name ("${brandName}"), e.g. "${brandName} Updates".`;
    }

    const descError = validateDescription(formData.short_description);
    if (descError) newErrors.short_description = descError;

    const colorError = validateHexColor(formData.theme_color);
    if (colorError) newErrors.theme_color = colorError;

    if (!isEditMode) {
      if (!logoFile && !bot?.bot_logo) newErrors.bot_logo = "Bot logo is required";
      if (!bannerFile && !bot?.banner_image) newErrors.banner_image = "Banner image is required";
    }

    if (phoneNumberOnly) {
      const callingCode = phoneCode.replace("+", "");
      const cleanedPhone = phoneNumberOnly.replace(/\D/g, "");
      const phoneError = getPhoneValidationError(cleanedPhone, getCountryForCallingCode(callingCode), callingCode);
      if (phoneError) newErrors.primary_phone = phoneError;
    }

    if (formData.primary_email) {
      const emailError = validateEmail(formData.primary_email);
      if (emailError) newErrors.primary_email = emailError;
    }

    if (formData.primary_website) {
      const urlError = validateUrl(formData.primary_website);
      if (urlError) newErrors.primary_website = urlError;
    }

    const termsError = validateUrl(formData.terms_of_use_url);
    if (termsError) newErrors.terms_of_use_url = termsError;
    else if (!formData.terms_of_use_url.trim()) newErrors.terms_of_use_url = "Terms of use URL is required";

    const privacyError = validateUrl(formData.privacy_policy_url);
    if (privacyError) newErrors.privacy_policy_url = privacyError;
    else if (!formData.privacy_policy_url.trim()) newErrors.privacy_policy_url = "Privacy policy URL is required";

    const languagesError = validateLanguages(formData.languages_supported);
    if (languagesError) newErrors.languages_supported = languagesError;

    if (!termsAccepted) {
      newErrors.terms = "You must accept the terms and conditions.";
    }
    if (formData.region === "INDIA" && !indianCarriersAccepted) {
      newErrors.indian_carriers = "You must agree to launch on all Indian carriers.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (status = "DRAFT") => {
    if (!validateForm()) return;
    setLoading(true);

    try {
      const submitData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key !== "primary_phone" && formData[key] !== null && formData[key] !== "") {
          submitData.append(key, formData[key]);
        }
      });

      // 🟢 NEW: The backend still expects 'brand_name'. 
      // Let's find it from the loaded brands and append it to the payload.
      const selectedBrandObj = brands.find((b) => b.id === formData.brand);
        if (selectedBrandObj) {
          submitData.append("brand_name", selectedBrandObj.brand_name);
        } else if (bot?.brand_details?.brand_name) {
          // Fallback for edit mode if brands haven't fully loaded
          submitData.append("brand_name", bot.brand_details.brand_name);
        } else if (lockedBrand) {
          submitData.append("brand_name", lockedBrand.brand_name);
        }
      // New brand is created by the backend together with the bot
      if (lockedBrand && !lockedBrand.id) submitData.append("new_brand_name", lockedBrand.brand_name);

      if (phoneNumberOnly) {
        const cleanedPhone = phoneNumberOnly.replace(/\D/g, "");
        submitData.append("primary_phone", `${phoneCode}${cleanedPhone}`);
      }

      submitData.set("status", status);

      if (logoFile) submitData.append("bot_logo", logoFile);
      if (bannerFile) submitData.append("banner_image", bannerFile);

      const url = isEditMode ? `${GET_RCS_BOTS}${bot.id}/` : POST_RCS_BOTS;
      const method = isEditMode ? "put" : "post";

      const response = await axios({
        method,
        url,
        data: submitData,
        headers: {
          // "Content-Type": "multipart/form-data",
          "X-Tenant-ID": localStorage.getItem("tenant") || "",
        },
      });

      onSuccess(response.data);
    } catch (error) {
      console.error("Error saving bot:", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || "Failed to save bot. Please try again.";

      if (error.response?.data) {
        const fieldErrors = {};
        Object.entries(error.response.data).forEach(([field, messages]) => {
          fieldErrors[field] = Array.isArray(messages) ? messages[0] : messages;
        });
        setErrors(fieldErrors);
      }
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const CHAR_LIMIT_BOT_NAME = 40;
  const CHAR_LIMIT_DESC = 100;
  const BILLING_OPTIONS = ["Conversational", "Non-Conversational"];
  const RCS_API_OPTIONS = [{ value: "GSMA", label: "GSMA API" }, { value: "GOOGLE", label: "Google RBM API" }];

  const MESSAGE_TYPE_OPTIONS = [
    { value: "PROMOTIONAL", label: "Promotional" },
    { value: "OTP", label: "OTP" },
    { value: "MULTI_USE", label: "Multi-Use" },
    { value: "TRANSACTIONAL", label: "Transactional" },
  ];

  const filteredMessageTypes = formData.region === "INDIA" 
    ? MESSAGE_TYPE_OPTIONS.filter(opt => opt.value !== "MULTI_USE")
    : MESSAGE_TYPE_OPTIONS;

  // Render variables for preview panel
  const previewBrandName = brands.find(b => b.id === formData.brand)?.brand_name || bot?.brand_details?.brand_name || "Brand Name";

  return (
    <Box sx={{ backgroundColor: ColorsConstants.primaryBlue60, minHeight: "100vh", fontFamily: "'Lexend', 'Roboto', sans-serif" }}>
      <Box sx={{ backgroundColor: ColorsConstants.primaryWhite, borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, px: 4, py: 1.5 }}>
        <Typography sx={{ ...typography.h1, color: ColorsConstants.primaryBlue100 }}>
          {isEditMode ? "Edit RCS Bot" : "Create RCS Bot"}
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 1280, mx: "auto", px: 3, py: 3, pb: "90px" }}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} lg={9}>
            <Box sx={CARD_SX}>
              
              <Box sx={{ mb: 3 }}>
                <Typography sx={SECTION_TITLE_SX}>
                  Region <Typography component="span" sx={{ color: ColorsConstants.error }}>*</Typography>
                </Typography>
                <RadioGroup row value={formData.region} onChange={handleChange("region")}>
                  {[ { value: "INDIA", label: "India" }, { value: "ROW", label: "Rest of World" } ].map((opt) => (
                    <FormControlLabel key={opt.value} value={opt.value} control={<Radio size="small" sx={RADIO_SX} />} label={<Typography sx={typography.bodyLarge}>{opt.label}</Typography>} />
                  ))}
                </RadioGroup>
              </Box>

              <Divider sx={{ mb: 3, borderColor: borderColors.cardBorderPrimaryGrayColor }} />

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
                  <Typography sx={SECTION_TITLE_SX}>
                    Bot Message Type <Typography component="span" sx={{ color: ColorsConstants.error }}>*</Typography>
                  </Typography>
                  <Tooltip title="Select the message type for your bot" arrow>
                    <InfoIcon sx={{ fontSize: 14, color: grayFontColor }} />
                  </Tooltip>
                </Box>
                <RadioGroup row value={formData.message_type} onChange={handleChange("message_type")}>
                  {filteredMessageTypes.map((opt) => (
                    <FormControlLabel
                      key={opt.value}
                      value={opt.value}
                      control={<Radio size="small" sx={RADIO_SX} />}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                          <Typography sx={typography.bodyLarge}>{opt.label}</Typography>
                          <Tooltip title={`${opt.label} bot messages`} arrow>
                            <InfoIcon sx={{ fontSize: 13, color: grayFontColor }} />
                          </Tooltip>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </Box>

              <Divider sx={{ mb: 3, borderColor: borderColors.cardBorderPrimaryGrayColor }} />

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                  <Typography sx={SECTION_TITLE_SX}>
                    Bot Billing Category <Typography component="span" sx={{ color: ColorsConstants.error }}>*</Typography>
                  </Typography>
                </Box>
                <Typography sx={HINT_SX}>Select the billing category for your chatbot.</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, maxWidth: 320 }}>
                  <FormControl fullWidth size="small">
                    <Select value={formData.billing_category} onChange={handleChange("billing_category")} input={<OutlinedInput />} sx={SELECT_SX}>
                      {BILLING_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt.toUpperCase().replace(/ /g, "_")} sx={typography.bodyLarge}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton size="small" onClick={() => setFormData((prev) => ({ ...prev, billing_category: "" }))} sx={{ color: grayFontColor, border: `1px solid ${ColorsConstants.inputFieldGrey}`, borderRadius: "8px" }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Divider sx={{ mb: 3, borderColor: borderColors.cardBorderPrimaryGrayColor }} />

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <FieldLabel required hint="The name users will see at the top of the message thread (40 chars max).">Bot Name</FieldLabel>
                  <TextField
                    value={formData.bot_name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, bot_name: e.target.value.slice(0, CHAR_LIMIT_BOT_NAME) }))}
                    placeholder="Enter bot name..." size="small" fullWidth inputProps={{ maxLength: CHAR_LIMIT_BOT_NAME }}
                    helperText={errors.bot_name || `${CHAR_LIMIT_BOT_NAME - formData.bot_name.length} characters left`}
                    error={!!errors.bot_name} sx={INPUT_SX}
                  />
                </Grid>

                {/* 🟢 UPDATED: Brand Field is now a Select Dropdown */}
                <Grid item xs={12} md={6}>
                  <FieldLabel required hint="Select a registered brand.">Brand</FieldLabel>
                  <FormControl fullWidth size="small" error={!!errors.brand}>
                    <Select
                      disabled={!!lockedBrand}
                      value={formData.brand}
                      onChange={handleChange("brand")}
                      input={<OutlinedInput />}
                      displayEmpty
                      sx={SELECT_SX}
                      renderValue={(selected) => {
                        if (!selected) {
                          if (lockedBrand) return <Typography sx={typography.bodyLarge}>{lockedBrand.brand_name}</Typography>;
                          return <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>Select Brand</Typography>;
                        }
                        const selectedBrand = brands.find(b => b.id === selected);
                        // Fallback to bot details if options haven't loaded yet
                        return <Typography sx={typography.bodyLarge}>{selectedBrand?.brand_name || bot?.brand_details?.brand_name || selected}</Typography>;
                      }}
                    >
                      {loadingBrands ? (
                        <MenuItem disabled><CircularProgress size={16} sx={{ mr: 1.5, color: ColorsConstants.primaryPurple100 }} /> Loading brands...</MenuItem>
                      ) : brands.length === 0 ? (
                        <MenuItem disabled>No brands found. Please create one first.</MenuItem>
                      ) : (
                        brands.map((b) => (
                          <MenuItem key={b.id} value={b.id} sx={typography.bodyLarge}>
                            {b.brand_name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                  {errors.brand && <ErrMsg>{errors.brand}</ErrMsg>}
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3, borderColor: borderColors.cardBorderPrimaryGrayColor }} />

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <FieldLabel required hint="Provide a logo for your bot displayed in connection with the bot's name.">Bot Logo</FieldLabel>
                  <ImageUploadZone 
                    aspectLabel="224px × 224px · Max 90KB · JPG/PNG" 
                    value={logoFile} 
                    preview={logoPreview} 
                    onChange={handleLogoChange} 
                    error={errors.bot_logo} 
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel required hint="Displayed on the bot's Info & Options screen. Your logo will be overlaid on the bottom center of this image.">Banner Image</FieldLabel>
                  <ImageUploadZone 
                    aspectLabel="1440px × 448px · Max 360KB · JPG/PNG" 
                    value={bannerFile} 
                    preview={bannerPreview} 
                    onChange={handleBannerChange} 
                    error={errors.banner_image} 
                  />
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3, borderColor: borderColors.cardBorderPrimaryGrayColor }} />

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <FieldLabel required>Short Description</FieldLabel>
                  <TextField value={formData.short_description} onChange={(e) => setFormData((prev) => ({ ...prev, short_description: e.target.value.slice(0, CHAR_LIMIT_DESC) }))} placeholder="Enter a brief description of your bot..." size="small" fullWidth multiline rows={3} inputProps={{ maxLength: CHAR_LIMIT_DESC }} helperText={errors.short_description || `${CHAR_LIMIT_DESC - formData.short_description.length} characters left`} error={!!errors.short_description} sx={MULTILINE_SX} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel required>Color</FieldLabel>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Box component="input" type="color" value={formData.theme_color} onChange={handleChange("theme_color")} sx={{ width: 40, height: 40, border: `1px solid ${ColorsConstants.inputFieldGrey}`, borderRadius: "8px", cursor: "pointer", padding: "3px", backgroundColor: ColorsConstants.primaryWhite }} />
                    <TextField value={formData.theme_color} onChange={handleChange("theme_color")} size="small" fullWidth error={!!errors.theme_color} helperText={errors.theme_color} sx={INPUT_SX} />
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3, borderColor: borderColors.cardBorderPrimaryGrayColor }} />

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <FieldLabel hint="Primary contact number for the bot.">Primary phone number</FieldLabel>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <CallingCodeSelect
                      value={phoneCode}
                      onChange={(code) => setPhoneCode(`+${code}`)}
                      label=""
                      size="small"
                      sx={{ width: 220, flexShrink: 0 }}
                      TextFieldProps={{ sx: INPUT_SX }}
                    />
                    <TextField
                      value={phoneNumberOnly}
                      onChange={(e) => setPhoneNumberOnly(e.target.value.replace(/\D/g, ""))}
                      placeholder="e.g. 9876543210"
                      size="small"
                      fullWidth
                      error={!!errors.primary_phone}
                      helperText={errors.primary_phone}
                      sx={INPUT_SX}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Label for primary phone number</FieldLabel>
                  <TextField value={formData.phone_label} onChange={handleChange("phone_label")} placeholder="e.g. Customer Support" size="small" fullWidth inputProps={{ maxLength: 20 }} helperText={`${20 - formData.phone_label.length} characters left`} sx={INPUT_SX} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FieldLabel>Primary email id</FieldLabel>
                  <TextField value={formData.primary_email} onChange={handleChange("primary_email")} placeholder="email@yourbrand.com" type="email" size="small" fullWidth error={!!errors.primary_email} helperText={errors.primary_email} sx={INPUT_SX} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Label for primary email id</FieldLabel>
                  <TextField value={formData.email_label} onChange={handleChange("email_label")} placeholder="e.g. Support" size="small" fullWidth inputProps={{ maxLength: 20 }} helperText={`${20 - formData.email_label.length} characters left`} sx={INPUT_SX} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FieldLabel>Primary website</FieldLabel>
                  <TextField value={formData.primary_website} onChange={handleChange("primary_website")} placeholder="https://yourbrand.com" size="small" fullWidth error={!!errors.primary_website} helperText={errors.primary_website} sx={INPUT_SX} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Label for primary website</FieldLabel>
                  <TextField value={formData.website_label} onChange={handleChange("website_label")} placeholder="e.g. Our Website" size="small" fullWidth inputProps={{ maxLength: 20 }} helperText={`${20 - formData.website_label.length} characters left`} sx={INPUT_SX} />
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3, borderColor: borderColors.cardBorderPrimaryGrayColor }} />

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <FieldLabel required hint="Enter the URL for the Terms of Use.">Terms of Use URL</FieldLabel>
                  <TextField value={formData.terms_of_use_url} onChange={handleChange("terms_of_use_url")} placeholder="https://yourbrand.com/terms" size="small" fullWidth type="url" error={!!errors.terms_of_use_url} helperText={errors.terms_of_use_url} sx={INPUT_SX} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel required hint="Enter the URL for Privacy Policy.">Privacy Policy URL</FieldLabel>
                  <TextField value={formData.privacy_policy_url} onChange={handleChange("privacy_policy_url")} placeholder="https://yourbrand.com/privacy" size="small" fullWidth type="url" error={!!errors.privacy_policy_url} helperText={errors.privacy_policy_url} sx={INPUT_SX} />
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3, borderColor: borderColors.cardBorderPrimaryGrayColor }} />

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <FieldLabel required>RCS API</FieldLabel>
                  <TextField
                    value="Google RBM API"
                    disabled
                    size="small"
                    fullWidth
                    sx={{
                      ...INPUT_SX,
                      "& .MuiInputBase-input.Mui-disabled": {
                        backgroundColor: ColorsConstants.primaryBlue60,
                        WebkitTextFillColor: inputColor,
                        opacity: 0.7,
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3, borderColor: borderColors.cardBorderPrimaryGrayColor }} />

              <Box sx={{ maxWidth: 420, mb: 3 }}>
                <FieldLabel required>Languages Supported</FieldLabel>
                <TextField value={formData.languages_supported} onChange={handleChange("languages_supported")} placeholder="e.g. English, Hindi, Marathi" size="small" fullWidth error={!!errors.languages_supported} helperText={errors.languages_supported} sx={INPUT_SX} />
              </Box>

              <Box sx={{ mt: 4, p: 2, backgroundColor: ColorsConstants.primaryBlue60, borderRadius: "8px", border: `1px solid ${errors.terms || errors.indian_carriers ? ColorsConstants.error : borderColors.cardBorderPrimaryGrayColor}` }}>
                <FormControlLabel
                  control={<Checkbox checked={termsAccepted} onChange={(e) => { setTermsAccepted(e.target.checked); setErrors(p => ({...p, terms: null})) }} sx={{ color: ColorsConstants.inputFieldGrey, '&.Mui-checked': { color: ColorsConstants.primaryPurple100 } }} />}
                  label={<Typography sx={{ ...typography.bodyLarge, color: errors.terms ? ColorsConstants.error : inputColor }}>I accept the Terms & Conditions and Privacy Policy for this bot.</Typography>}
                />
                {errors.terms && <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.error, ml: 4, mt: -1, mb: 1 }}>{errors.terms}</Typography>}
                
                {formData.region === "INDIA" && (
                  <>
                    <FormControlLabel
                      control={<Checkbox checked={indianCarriersAccepted} onChange={(e) => { setIndianCarriersAccepted(e.target.checked); setErrors(p => ({...p, indian_carriers: null})) }} sx={{ color: ColorsConstants.inputFieldGrey, '&.Mui-checked': { color: ColorsConstants.primaryPurple100 } }} />}
                      label={<Typography sx={{ ...typography.bodyLarge, color: errors.indian_carriers ? ColorsConstants.error : inputColor }}>I agree to launch the bot on all Indian carriers.</Typography>}
                      sx={{ mt: 1 }}
                    />
                    {errors.indian_carriers && (
                      <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.error, ml: 4, mt: -1, mb: 1 }}>
                        {errors.indian_carriers}
                      </Typography>
                    )}
                  </>
                )}
              </Box>

            </Box>
          </Grid>

          <Grid item xs={12} lg={3}>
            <Box sx={{ ...CARD_SX, position: "sticky", top: 20 }}>
              <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: grayFontColor, textAlign: "center", mb: 2, textTransform: "uppercase", letterSpacing: "0.07em", fontSize: "11px" }}>
                Preview of Business Info
              </Typography>
              <PhonePreview botName={formData.bot_name} description={formData.short_description} logoPreview={logoPreview} bannerPreview={bannerPreview} color={formData.theme_color} />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{  bottom: 0, left: 0, right: 0, backgroundColor: ColorsConstants.primaryWhite, borderTop: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, px: 4, py: 1.5, zIndex: 100, boxShadow: "0 -2px 12px rgba(8,36,92,0.07)" }}>
        <Box sx={{ maxWidth: 1280, mx: "auto", display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <ButtonOne variant="outlined" onClick={onBack} disabled={loading}>Cancel</ButtonOne>
          <ButtonTwo variant="outlined" onClick={() => handleSubmit("DRAFT")} disabled={loading} >
            {loading ? "Saving..." : "Save as Draft"}
          </ButtonTwo>
          <ButtonOne variant="contained" onClick={() => handleSubmit("SUBMITTED")} disabled={loading}>
            {loading ? "Submitting..." : isEditMode ? "Update Bot" : "Create Bot"}
          </ButtonOne>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ position: "fixed", bottom: 80, right: 24, zIndex: 200, display: "flex", alignItems: "center", gap: 1, backgroundColor: ColorsConstants.primaryWhite, px: 2, py: 1, borderRadius: "8px", boxShadow: "0 2px 12px rgba(0,0,0,0.1)" }}>
          <CircularProgress size={20} sx={{ color: ColorsConstants.primaryBlue100 }} />
          <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryBlue100 }}>{isEditMode ? "Updating bot..." : "Creating bot..."}</Typography>
        </Box>
      )}
    </Box>
  );
}